import { http, withoutAccessHttp } from '../http-common';

export type MailingCampaignStatus = 'DRAFT' | 'SENDING' | 'PAUSED' | 'COMPLETED' | 'CANCELLED';
export type MailingLogStatus = 'SENT' | 'FAILED';
export type MailingContactSource = 'MANUAL' | 'IMPORT';
export type MailingSurveyQuestionType = 'TEXT' | 'MULTIPLE_CHOICE';
export type MailingSurveyRecipientSendStatus = 'PENDING' | 'SENT' | 'FAILED' | 'SKIPPED';

export interface MailingContact {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email: string;
  phone?: string | null;
  companyName?: string | null;
  tags?: string[] | null;
  source?: MailingContactSource | null;
  active?: boolean;
  deletedAt?: string | null;
  createdAt?: string | null;
}

export interface MailingTemplate {
  id: string;
  name: string;
  subject: string;
  content: string;
  active?: boolean;
  deletedAt?: string | null;
}

export interface MailingCampaign {
  id: string;
  name: string;
  template?: MailingTemplate | null;
  delayMinutes: number;
  status: MailingCampaignStatus;
  currentIndex: number;
  lastSentAt?: string | null;
  scheduledStartAt?: string | null;
  sendWindowStartTime?: string | null;
  sendWindowEndTime?: string | null;
  plannedEndAt?: string | null;
  contactIds?: string[] | null;
  recipientCount?: number | null;
}

export interface MailingLog {
  id: string;
  campaign?: MailingCampaign | null;
  contact?: MailingContact | null;
  status: MailingLogStatus;
  errorMessage?: string | null;
  sentAt?: string | null;
}

export interface MailingSurveyOption {
  id?: string;
  optionText: string;
  isOther?: boolean;
  orderNo?: number;
}

export interface MailingSurveyQuestion {
  id?: string;
  questionText: string;
  questionType: MailingSurveyQuestionType;
  required?: boolean;
  orderNo?: number;
  options?: MailingSurveyOption[];
}

export interface MailingSurvey {
  id: string;
  name: string;
  description?: string | null;
  active?: boolean;
  deletedAt?: string | null;
  createdAt?: string | null;
  questions: MailingSurveyQuestion[];
}

export interface MailingSurveyCampaign {
  id: string;
  name: string;
  survey?: MailingSurvey | null;
  template?: MailingTemplate | null;
  delayMinutes: number;
  status: MailingCampaignStatus;
  currentIndex: number;
  lastSentAt?: string | null;
  scheduledStartAt?: string | null;
  sendWindowStartTime?: string | null;
  sendWindowEndTime?: string | null;
  plannedEndAt?: string | null;
  responseDeadlineAt?: string | null;
  recipientCount?: number | null;
  contactIds?: string[] | null;
  createdAt?: string | null;
}

export interface MailingSurveyCampaignReportSummary {
  campaignId: string;
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  skippedCount: number;
  pendingCount: number;
  answeredCount: number;
  unansweredCount: number;
  answerRate: number;
}

export interface MailingSurveyQuestionOptionReport {
  optionId?: string;
  optionText: string;
  isOther?: boolean;
  count: number;
}

export interface MailingSurveyQuestionReport {
  questionId: string;
  questionText: string;
  questionType: MailingSurveyQuestionType;
  totalAnswers: number;
  otherTextCount: number;
  options: MailingSurveyQuestionOptionReport[];
}

export interface MailingSurveyRecipientReport {
  recipientId: string;
  contactId?: string;
  sequenceNo?: number;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  companyName?: string | null;
  source?: MailingContactSource | null;
  tags?: string[] | null;
  sendStatus: MailingSurveyRecipientSendStatus;
  sentAt?: string | null;
  answeredAt?: string | null;
  errorMessage?: string | null;
  tokenExpiresAt?: string | null;
}

export interface MailingSurveyPublicOption {
  id: string;
  optionText: string;
  isOther?: boolean;
}

export interface MailingSurveyPublicQuestion {
  id: string;
  questionText: string;
  questionType: MailingSurveyQuestionType;
  required?: boolean;
  options: MailingSurveyPublicOption[];
}

export interface MailingSurveyPublicSurvey {
  campaignName: string;
  surveyName: string;
  responseDeadlineAt?: string | null;
  questions: MailingSurveyPublicQuestion[];
}

export interface MailingSurveyPublicSubmitResult {
  applied: boolean;
  message: string;
}

export interface CreateMailingContactRequest {
  firstName?: string | null;
  lastName?: string | null;
  email: string;
  phone?: string | null;
  companyName?: string | null;
  tags?: string[] | null;
}

export interface UpdateMailingContactRequest {
  firstName?: string | null;
  lastName?: string | null;
  email: string;
  phone?: string | null;
  companyName?: string | null;
  tags?: string[] | null;
}

export interface CreateMailingTemplateRequest {
  name: string;
  subject: string;
  content: string;
}

export interface UpdateMailingTemplateRequest {
  name: string;
  subject: string;
  content: string;
}

export interface CreateMailingCampaignRequest {
  name: string;
  templateId: string;
  delayMinutes: number;
  contactIds: string[];
}

export interface StartMailingCampaignRequest {
  startDate: string;
  sendWindowStartTime: string;
  sendWindowEndTime: string;
}

export interface CreateMailingSurveyRequest {
  name: string;
  description?: string | null;
  questions: MailingSurveyQuestion[];
}

export interface UpdateMailingSurveyRequest {
  name: string;
  description?: string | null;
  questions: MailingSurveyQuestion[];
}

export interface CreateMailingSurveyCampaignRequest {
  name: string;
  surveyId: string;
  templateId: string;
  delayMinutes: number;
  contactIds: string[];
}

export interface StartMailingSurveyCampaignRequest {
  startDate: string;
  sendWindowStartTime: string;
  sendWindowEndTime: string;
  responseDeadlineAt: string;
}

export interface MailingSurveyPublicAnswerRequest {
  questionId: string;
  selectedOptionId?: string;
  answerText?: string;
  otherText?: string;
}

export interface MailingSurveyPublicSubmitRequest {
  answers: MailingSurveyPublicAnswerRequest[];
}

export interface MailingContactImportError {
  rowNumber: number;
  email?: string | null;
  message: string;
}

export interface MailingContactImportResult {
  totalRows: number;
  createdCount: number;
  skippedDuplicateCount: number;
  skippedInvalidCount: number;
  errors: MailingContactImportError[];
}

const getContacts = (includePassive = false) =>
  http.get<MailingContact[]>('/mailing/contacts', { params: { includePassive } });
const createContact = (data: CreateMailingContactRequest) =>
  http.post<MailingContact>('/mailing/contacts', data);
const updateContact = (contactId: string, data: UpdateMailingContactRequest) =>
  http.put<MailingContact>(`/mailing/contacts/${contactId}`, data);
const updateContactStatus = (contactId: string, active: boolean) =>
  http.patch<MailingContact>(`/mailing/contacts/${contactId}/status`, { active });
const deleteContact = (contactId: string) =>
  http.delete<void>(`/mailing/contacts/${contactId}`);
const importContacts = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return http.post<MailingContactImportResult>('/mailing/contacts/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

const getTemplates = (includePassive = false) =>
  http.get<MailingTemplate[]>('/mailing/templates', { params: { includePassive } });
const createTemplate = (data: CreateMailingTemplateRequest) =>
  http.post<MailingTemplate>('/mailing/templates', data);
const updateTemplate = (templateId: string, data: UpdateMailingTemplateRequest) =>
  http.put<MailingTemplate>(`/mailing/templates/${templateId}`, data);
const updateTemplateStatus = (templateId: string, active: boolean) =>
  http.patch<MailingTemplate>(`/mailing/templates/${templateId}/status`, { active });

const getCampaigns = () => http.get<MailingCampaign[]>('/mailing/campaigns');
const createCampaign = (data: CreateMailingCampaignRequest) =>
  http.post<MailingCampaign>('/mailing/campaigns', data);
const startCampaign = (campaignId: string, data: StartMailingCampaignRequest) =>
  http.post<void>(`/mailing/campaigns/${campaignId}/start`, data);
const stopCampaign = (campaignId: string) =>
  http.post<void>(`/mailing/campaigns/${campaignId}/stop`);
const cancelCampaign = (campaignId: string) =>
  http.post<void>(`/mailing/campaigns/${campaignId}/cancel`);
const getCampaignLogs = (campaignId: string) =>
  http.get<MailingLog[]>(`/mailing/campaigns/${campaignId}/logs`);

const getSurveys = (includePassive = false) =>
  http.get<MailingSurvey[]>('/mailing/surveys', { params: { includePassive } });
const createSurvey = (data: CreateMailingSurveyRequest) =>
  http.post<MailingSurvey>('/mailing/surveys', data);
const updateSurvey = (surveyId: string, data: UpdateMailingSurveyRequest) =>
  http.put<MailingSurvey>(`/mailing/surveys/${surveyId}`, data);
const updateSurveyStatus = (surveyId: string, active: boolean) =>
  http.patch<MailingSurvey>(`/mailing/surveys/${surveyId}/status`, { active });

const getSurveyCampaigns = () =>
  http.get<MailingSurveyCampaign[]>('/mailing/survey-campaigns');
const createSurveyCampaign = (data: CreateMailingSurveyCampaignRequest) =>
  http.post<MailingSurveyCampaign>('/mailing/survey-campaigns', data);
const startSurveyCampaign = (campaignId: string, data: StartMailingSurveyCampaignRequest) =>
  http.post<void>(`/mailing/survey-campaigns/${campaignId}/start`, data);
const stopSurveyCampaign = (campaignId: string) =>
  http.post<void>(`/mailing/survey-campaigns/${campaignId}/stop`);
const cancelSurveyCampaign = (campaignId: string) =>
  http.post<void>(`/mailing/survey-campaigns/${campaignId}/cancel`);

const getSurveyReportSummary = (campaignId: string) =>
  http.get<MailingSurveyCampaignReportSummary>(`/mailing/survey-campaigns/${campaignId}/report/summary`);
const getSurveyReportQuestions = (campaignId: string) =>
  http.get<MailingSurveyQuestionReport[]>(`/mailing/survey-campaigns/${campaignId}/report/questions`);
const getSurveyReportRecipients = (campaignId: string) =>
  http.get<MailingSurveyRecipientReport[]>(`/mailing/survey-campaigns/${campaignId}/report/recipients`);

const getPublicSurveyByToken = (token: string) =>
  withoutAccessHttp.get<MailingSurveyPublicSurvey>(`/mailing/public/surveys/${encodeURIComponent(token)}`);
const submitPublicSurveyResponse = (token: string, data: MailingSurveyPublicSubmitRequest) =>
  withoutAccessHttp.post<MailingSurveyPublicSubmitResult>(`/mailing/public/surveys/${encodeURIComponent(token)}/responses`, data);

const MailingService = {
  getContacts,
  createContact,
  updateContact,
  updateContactStatus,
  deleteContact,
  importContacts,
  getTemplates,
  createTemplate,
  updateTemplate,
  updateTemplateStatus,
  getCampaigns,
  createCampaign,
  startCampaign,
  stopCampaign,
  cancelCampaign,
  getCampaignLogs,
  getSurveys,
  createSurvey,
  updateSurvey,
  updateSurveyStatus,
  getSurveyCampaigns,
  createSurveyCampaign,
  startSurveyCampaign,
  stopSurveyCampaign,
  cancelSurveyCampaign,
  getSurveyReportSummary,
  getSurveyReportQuestions,
  getSurveyReportRecipients,
  getPublicSurveyByToken,
  submitPublicSurveyResponse,
};

export default MailingService;
